/**
 * What becomes of OS's live call when a box that draws its thread goes away
 * (os-live-mode.md, Decided 14: switching Layout keeps live mode going).
 *
 * The call is held by `CoachCallHost`, above every page, and drawn by the
 * conversation's composer. Layout to Full page, or back to Floating or Side
 * sheet, takes that composer down and draws another on the new place: the
 * switch says it is carrying the thread (`carrying`), and the call waits up
 * to `CARRY_MS` for the new box. Any other leave (Close, a page that no
 * longer shows the thread) ends live mode, as it always has. Once live mode
 * has stopped and only an answer is still owed (`onlyOwed`), a box going
 * keeps it: the answer lands in the thread, which outlives every box (C2, C3).
 */

/** How long a carried call waits for the box the new place draws: a route change, with its page's first read. */
export const CARRY_MS = 3000;

export type CallRelease = 'keep' | 'wait' | 'end';

export const callReleaseOf = (input: {
  /** The thread the call (or an answer it still owes) is on, or null with none. */
  readonly callThread: string | null;
  /** The thread of the box that went. */
  readonly released: string;
  /** Boxes still drawing that thread. */
  readonly holdersLeft: number;
  /** The thread a Layout switch is moving now, or null. */
  readonly carrying: string | null;
  /** Live mode has stopped; the call only owes its thread an answer, which still lands. */
  readonly onlyOwed?: boolean;
}): CallRelease => {
  const { callThread, released, holdersLeft, carrying, onlyOwed = false } = input;
  if (callThread === null || callThread !== released || holdersLeft > 0 || onlyOwed) return 'keep';
  return carrying === released ? 'wait' : 'end';
};

/**
 * Whether the conversation, and so its cards, is in front of the person
 * (step 3.11b): not while OS is minimised and not while the tab is hidden. A
 * spoken yes then presses nothing. Minimize keeps the call (its box stays
 * mounted, out of sight); Close takes the box down, and the call with it.
 */
export const conversationShownOf = ({ minimised, tabHidden }: { readonly minimised: boolean; readonly tabHidden: boolean }): boolean => !(minimised || tabHidden);

/** The setup seat a holder's call opens on (step 5.1): with no tenant, a new workspace or Edit the setup on its session. */
export type CallSeat = 'setup';

/**
 * The business a holder's call runs in: none on the setup seat, which
 * composes a business that does not exist yet (step 5.2); the thread's own
 * on every other.
 */
export const callTenantOf = (holder: { readonly seat?: CallSeat; readonly thread: { readonly tenantId: string } }): string | null =>
  holder.seat === 'setup' ? null : holder.thread.tenantId;

/**
 * What a seat holder's start says beside its thread: the seat, the setup's
 * own screen (no business place to read), and `after: 'create'` when live
 * mode carries on after Create rather than starting (no greeting, no chime,
 * one ready line). Null for an assistant holder: its place is read from the
 * page.
 */
export const seatContextOf = (holder: {
  readonly seat?: CallSeat;
  readonly after?: 'create';
  readonly setupSession?: string;
  /** The studio tab the call opens from (9.4): the call holds the setup's lease for it. */
  readonly setupClient?: string;
}): { readonly screen?: string; readonly seat?: CallSeat; readonly after?: 'create'; readonly setupSession?: { readonly id: string; readonly client?: string } } => ({
  ...(holder.seat === undefined ? {} : { screen: 'setup', seat: holder.seat }),
  ...(holder.after === undefined ? {} : { after: holder.after }),
  // The studio's session rides only with the setup seat (7.5); without it the seat is the phone's legacy one.
  ...(holder.seat === 'setup' && holder.setupSession !== undefined && holder.setupSession !== '' ? { setupSession: { id: holder.setupSession, ...(holder.setupClient === undefined ? {} : { client: holder.setupClient }) } } : {}),
});
